import * as React from "react";
import { UserAreaPermissions } from "../../../../models/UserAreaPermissions";
import "./TripleSelectBox.tsx.css"

interface Props {
    leftTitle?: string;
    centerTitle?: string;
    rightTitle?: string;
    disabled: boolean;

    onChange?: () => void;

    options: TripleSelectBoxOption[];
    selected: { id: number, permissions: UserAreaPermissions }[];

    isCenter: (option: TripleSelectBoxOption) => boolean;
    isRight: (option: TripleSelectBoxOption) => boolean;

    moveToLeft: (options: TripleSelectBoxOption[]) => void;
    moveToCenter: (options: TripleSelectBoxOption[]) => void;
    moveToRight: (options: TripleSelectBoxOption[]) => void;

    disabledOption: (optionValue: number) => boolean;

    moveToCenterDisabled: (options: number[]) => boolean;
}

interface State {
    activeValues: number[];
}

export interface TripleSelectBoxOption {
    value: number,
    label: string
}

export class TripleSelectBox extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            activeValues: []
        };

        this.moveToLeftOnClick = this.moveToLeftOnClick.bind(this);
        this.moveToCenterOnClick = this.moveToCenterOnClick.bind(this);
        this.moveToRightOnClick = this.moveToRightOnClick.bind(this);

        this.getLeftOptions = this.getLeftOptions.bind(this);
        this.getCenterOptions = this.getCenterOptions.bind(this);
        this.getRightOptions = this.getRightOptions.bind(this);
    }

    onChange(e: React.FormEvent<HTMLSelectElement>) {
        let values = Array.from(e.currentTarget.selectedOptions, option => parseInt(option.value));
        this.setState({ activeValues: values })
    }

    getSelectedOptions(): TripleSelectBoxOption[] {
        const { options } = this.props;
        const { activeValues } = this.state;

        return options.filter(o => activeValues.some(av => av == o.value));
    }

    isSelectedOption(option: TripleSelectBoxOption): boolean {
        const { selected } = this.props;

        return selected.some(s => s.id === option.value);
    }

    moveToLeftOnClick(): void {
        const { moveToLeft } = this.props;
        const selectedOptions = this.getSelectedOptions();

        if (!selectedOptions.length) {
            return;
        }

        moveToLeft(selectedOptions);
        this.resetActiveValues();
    }

    moveToCenterOnClick(): void {
        const { moveToCenter } = this.props;
        const selectedOptions = this.getSelectedOptions();

        if (!selectedOptions.length) {
            return;
        }

        moveToCenter(selectedOptions);
        this.resetActiveValues();
    }

    moveToRightOnClick(): void {
        const { moveToRight } = this.props;
        const selectedOptions = this.getSelectedOptions();

        if (!selectedOptions.length) {
            return;
        }

        moveToRight(selectedOptions);
        this.resetActiveValues();
    }

    getLeftOptions(): TripleSelectBoxOption[] {
        const { options } = this.props;
        return options.filter(o => !this.isSelectedOption(o));
    }

    getCenterOptions(): TripleSelectBoxOption[] {
        const { options, isCenter } = this.props;
        return options.filter(o => isCenter(o));
    }

    getRightOptions(): TripleSelectBoxOption[] {
        const { options, isRight } = this.props;
        return options.filter(o => isRight(o));
    }

    resetActiveValues(): void {
        this.setState({ activeValues: [] });
    }

    render() {
        const { leftTitle, centerTitle, rightTitle, disabled, disabledOption, moveToCenterDisabled } = this.props;
        const { activeValues } = this.state;

        const leftOptions = this.getLeftOptions();
        const centerOptions = this.getCenterOptions();
        const rightOptions = this.getRightOptions();

        return (
            <div className="triple-select-box-wrapper">
                <section className="select-box-container" >
                    <label className="select-box-label">{leftTitle}</label>
                    <select
                        disabled={disabled}
                        className="select-box"
                        onChange={e => this.onChange(e)}
                        multiple
                        size={10}
                    >
                        {leftOptions.map(({ value, label }) => (
                            <option
                                disabled={disabledOption(value)}
                                key={value}
                                value={value}
                            >
                                {label ? label : value}
                            </option>
                        ))}
                    </select>
                </section>
                <div className="buttons">
                    <button type="button" disabled={disabled} onClick={this.moveToLeftOnClick}>&#9664;</button>
                    <button type="button" disabled={disabled || moveToCenterDisabled(activeValues)} onClick={this.moveToCenterOnClick}>&#9658;</button>
                </div>
                <section className="select-box-container" >
                    <label className="select-box-label">{centerTitle}</label>
                    <select
                        disabled={disabled}
                        className="select-box"
                        onChange={e => this.onChange(e)}
                        multiple
                        size={10}
                    >
                        {centerOptions.map(({ value, label }) => (
                            <option
                                disabled={disabledOption(value)}
                                key={value}
                                value={value}
                            >
                                {label ? label : value}
                            </option>
                        ))}
                    </select>
                </section>
                <div className="buttons">
                    <button type="button" disabled={disabled || moveToCenterDisabled(activeValues)} onClick={this.moveToCenterOnClick}>&#9664;</button>
                    <button type="button" disabled={disabled} onClick={this.moveToRightOnClick}>&#9658;</button>
                </div>
                <section className="select-box-container" >
                    <label className="select-box-label">{rightTitle}</label>
                    <select
                        disabled={disabled}
                        className="select-box"
                        onChange={e => this.onChange(e)}
                        multiple
                        size={10}
                    >
                        {rightOptions.map(({ value, label }) => (
                            <option
                                disabled={disabledOption(value)}
                                key={value}
                                value={value}
                            >
                                {label ? label : value}
                            </option>
                        ))}
                    </select>
                </section>
            </div>
        );
    }
}
